import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Fade,
  IconButton,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import { format } from "date-fns";
import React, { useEffect, useRef, useState } from "react";
import {
  TASK_COMMENTS_BY_TASK_URL,
  TASK_COMMENTS_URL,
} from "../../Constants/Constants";
import { isEmpty } from "../../Constants/RolesConstants";
import { USER_NAME } from "../../Constants/StorageConstants";
import { getUserName } from "../../services/cacheService";
import { ThemeProvider } from "../v2/home/theme/ThemeProvider";
import AxiosInstance from "./../../services/AxiosInstance";
import { useAlert } from "./../alerts//AlertContext";

function stringAvatar(name) {
  if (!name) return "";
  const parts = name.split(" ").filter(Boolean);
  return parts.length > 1
    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    : `${parts[0][0]}`.toUpperCase();
}

// Helper to highlight mentions (e.g., @Name)
function renderCommentText(text, theme) {
  if (!text) return "";
  const mentionRegex = /(@[A-Za-z0-9_ ]+)/g;
  const parts = text.split(mentionRegex);
  return parts.map((part, idx) =>
    mentionRegex.test(part) ? (
      <Box
        key={idx}
        component="span"
        sx={{
          bgcolor: theme.palette.primary.light + "30",
          color: theme.palette.primary.main,
          px: 0.5,
          py: 0.1,
          borderRadius: 1,
          fontWeight: 600,
          fontSize: "0.9em",
          mr: 0.5,
        }}
      >
        {part}
      </Box>
    ) : (
        <React.Fragment key={idx}>{part}</React.Fragment>
      )
  );
}

function getRelativeTime(dateString, preText = "", postText = "ago") {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return `just now`;
  if (diff < 3600)
    return `${preText} ${Math.floor(diff / 60)} minutes ${postText}`;
  if (diff < 86400)
    return `${preText} ${Math.floor(diff / 3600)} hours ${postText}`;
  return `${preText} ${format(date, "MMM d, yyyy")}`;
}

function TaskComments(props) {
  const theme = useTheme();
  const showAlert = useAlert();
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCommentId, setNewCommentId] = useState(null);
  const [posting, setPosting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [commentObj, setCommentObj] = useState({
    id: "",
    name: "",
    createdBy: "",
    task: {},
  });
  const listRef = useRef(null);

  const handleCheckboxChange = async (id) => {
    const updatedComments = comments.map((c) =>
      c.id === id ? { ...c, userVisible: !c.userVisible } : c
    );
    setComments(updatedComments);

    const updatedComment = updatedComments.find((c) => c.id === id);
    try {
      await AxiosInstance.put(TASK_COMMENTS_URL, {
        id: updatedComment.id,
        userVisible: updatedComment.userVisible,
      });
      setSnackbar({
        open: true,
        message: "Visibility updated!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to update visibility",
        severity: "error",
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    AxiosInstance.get(`${TASK_COMMENTS_BY_TASK_URL}/${props.taskId}`).then(
      (response) => {
        console.log(response.data.data);
        setComments(response.data.data);
        setLoading(false);
      }
    );
  }, []);

  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  const handlePostComment = () => {
    //alert(localStorage.getItem(USER_NAME));
    commentObj.id = "";
    commentObj.comment = comment;
    commentObj.createdBy = localStorage.getItem(USER_NAME);
    commentObj.task.id = props.taskId;
    //setCommentObj(commentObj);
    postComment(commentObj);
  };

  const postComment = (commentObj) => {
    AxiosInstance.post(TASK_COMMENTS_URL, commentObj)
      .then((response) => {
        var tempComment = response.data.data;
        console.log("Comment created successfully");
        console.log(response.data);
        commentObj.id = tempComment.id;
        commentObj.comment = tempComment.comment;
        commentObj.createdBy = tempComment.createdBy;
        commentObj.createdOn = tempComment.createdOn;
        commentObj.task.id = tempComment.task.id;
        comments.concat(commentObj);
        setComments([...comments, commentObj]);
        setComment("");
        setCommentObj({
          id: "",
          comment: "",
          createdBy: "",
          task: {},
        });
        //setComments(comments);

        console.log(comments);
        showAlert("Comment created successfully!", "success");
      })
      .catch((error) => {
        showAlert("Comment creation failed!", "error");
        console.error(error);
      });
  };
  return (
    <ThemeProvider>
      <Box
        sx={{
          width: "100%",
          height: "calc(100% - 50px)",
          bgcolor: theme.palette.background.default,
          borderRadius: 3,
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          p: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Comments List */}
        <Box
          ref={listRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            bgcolor: theme.palette.background.default,
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              bgcolor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: theme.palette.divider,
              borderRadius: "3px",
              "&:hover": {
                bgcolor: theme.palette.text.secondary,
              },
            },
          }}
        >
          {loading ? (
            <Box sx={{ p: 2 }}>
              {[...Array(3)].map((_, i) => (
                <Box key={i} sx={{ display: "flex", gap: 1.5, mb: 2 }}>
                  <Skeleton variant="circular" width={32} height={32} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="40%" height={16} />
                    <Skeleton variant="text" width="80%" height={14} />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : comments.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 4,
                px: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No comments yet. Be the first to comment!
              </Typography>
            </Box>
          ) : (
                <Box sx={{ p: 2 }}>
                  {comments.map((comment, index) => (
                    <Fade
                      in={true}
                      timeout={comment.id === newCommentId ? 600 : 300}
                      key={comment.id}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                          mb: 2,
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor:
                          comment.id === newCommentId
                            ? theme.palette.primary.light + "10"
                            : "transparent",
                          border:
                          comment.id === newCommentId
                            ? `1px solid ${theme.palette.primary.light}`
                            : "transparent",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <Avatar
                          alt={comment.createdBy}
                          src={`/static/images/avatar/${comment.id}.jpg`}
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: theme.palette.primary.main,
                            fontWeight: 600,
                            fontSize: 14,
                            color: theme.palette.getContrastText(
                              theme.palette.primary.main
                            ),
                          }}
                        >
                          {stringAvatar(comment.createdBy)}
                        </Avatar>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.5,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography
                              fontWeight={600}
                              variant="body2"
                              sx={{
                                color: theme.palette.text.primary,
                                fontSize: "0.875rem",
                              }}
                            >
                              {comment.createdBy}
                            </Typography>

                            <Stack direction="row" spacing={1} alignItems="center">
                              <AccessTimeIcon
                                sx={{
                                  fontSize: 14,
                                  color: theme.palette.text.secondary,
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ color: theme.palette.text.secondary }}
                              >
                                Created {getRelativeTime(comment.createdOn)}
                              </Typography>
                              {comment.modifiedOn &&
                                comment.modifiedOn !== comment.createdOn && (
                              <>
                                  <EditIcon
                                    sx={{
                                      fontSize: 14,
                                      color: theme.palette.info.main,
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{ color: theme.palette.info.main }}
                                  >
                                    Last updated{" "}
                                    {getRelativeTime(comment.modifiedOn)}
                                  </Typography>
                              </>
                            )}
                          {!comment.userVisible && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: theme.palette.warning.main,
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                  }}
                                >
                                  • Private
                            </Typography>
                              )}
                            </Stack>

                            <Box sx={{ flex: 1 }} />

                            <Tooltip
                              title={
                                comment.userVisible
                                  ? "Visible to users"
                                  : "Private comment"
                              }
                              arrow
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleCheckboxChange(comment.id)}
                                disabled={
                                  !(
                                    isEmpty(props ?.componentPermissions) ||
                                      props ?.componentPermissions ?.["canUpdate"]
                              )
                                }
                                sx={{
                                  p: 0.5,
                                  color: comment.userVisible
                                    ? theme.palette.success.main
                                    : theme.palette.text.secondary,
                                  "&:hover": {
                                    bgcolor: comment.userVisible
                                      ? theme.palette.success.light + "20"
                                      : theme.palette.action.hover,
                                  },
                                }}
                              >
                                {comment.userVisible ? (
                                  <VisibilityIcon sx={{ fontSize: 16 }} />
                                ) : (
                                    <VisibilityOffIcon sx={{ fontSize: 16 }} />
                                  )}
                              </IconButton>
                            </Tooltip>
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.primary,
                              fontSize: "0.875rem",
                              lineHeight: 1.4,
                              whiteSpace: "pre-line",
                              wordBreak: "break-word",
                            }}
                          >
                            {renderCommentText(comment.comment, theme)}
                          </Typography>
                        </Box>
                      </Box>
                    </Fade>
                  ))}
                </Box>
              )}
        </Box>

        {/* Comment Input - Sticky at bottom */}
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: theme.palette.background.paper,
            boxShadow: "0 -2px 12px rgba(0, 0, 0, 0.1)",
            borderTop: `1px solid ${theme.palette.divider}`,
            px: { xs: 1, sm: 3 },
            py: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 1,
              mx: "auto",
              bgcolor: theme.palette.background.default,
              borderRadius: 4,
            }}
          >
            <Avatar
              alt={getUserName()}
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 48,
                height: 48,
                fontWeight: 600,
                fontSize: 18,
                margin: "0 0 10px 10px",
                color: theme.palette.getContrastText(
                  theme.palette.primary.main
                ),
              }}
            >
              {stringAvatar(getUserName())}
            </Avatar>
            <TextField
              fullWidth
              size="medium"
              variant="standard"
              placeholder="Write a comment...(press Enter to post or Shift+Enter for new line)"
              value={comment}
              onChange={handleCommentChange}
              multiline
              minRows={2}
              maxRows={2}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handlePostComment();
                }
              }}
              disabled={posting}
            />
            <Button
              variant="contained"
              size="large"
              endIcon={
                posting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                    <SendIcon />
                  )
              }
              onClick={handlePostComment}
              disabled={!comment.trim() || posting}
              sx={{
                minWidth: 120,
                height: 48,
                borderRadius: 3,
                fontWeight: 700,
                fontSize: "1rem",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                margin: "0 10px 10px 0",
                textTransform: "none",
              }}
            >
              {posting ? "" : "Post"}
            </Button>
          </Box>
        </Box>
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{
              width: "100%",
              borderRadius: 1,
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}

export default TaskComments;
